import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { getDeviceId, generateDeviceSign, getSessionToken } from "@/utils/appSecurity";
import { isIntegrityCompromised } from "@/utils/integrityGuard";
import { SERVICE_BASE_URL } from "@/config";
import { getApiBase, getYstHtmlReferer, getStoredPlatformId, PLATFORM_JQ18, PLATFORM_WUYOU } from "@/utils/apiPlatform";
import { getLstToken } from "@/utils/lstSessionStorage";
import { isAuthTokenExpired } from "@/utils/authToken";
import { getAutoPullDelayMs, sleepMs } from "@/utils/autoPullInterval";
let _tokenExpiredHandler = null;
export const setTokenExpiredHandler = (fn) => {
    _tokenExpiredHandler = fn;
};
const getHttp = () => {
    if (Capacitor.isNativePlatform()) {
        return CapacitorHttp;
    }
    return null;
};
const getTransId = () => {
    return "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx".replace(/x/g, () => Math.floor(Math.random() * 16).toString(16));
};
const _isRetryableResponse = (resData) => {
    if (!resData || typeof resData !== "object")
        return true;
    const c = resData.code;
    if (c === 1)
        return false;
    const m = resData.message || "";
    return /频繁|限流|限制|请求过多|rate|too many|throttl/i.test(m);
};
const apiRequest = async (baseUrl, path, method = "GET", data = null, token = "", phone = "") => {
    if (getStoredPlatformId() === PLATFORM_WUYOU && String(path).includes("/nft-web"))
        return { code: 0, message: "\u5F53\u524D\u4E3A\u65E0\u5FE7\u5E73\u53F0\uFF0C\u8BF7\u4F7F\u7528\u5B98\u65B9 API\uFF08\u5982\u6301\u4ED3\u9875\u5DF2\u63A5\u5165 /market/app/market/list\uFF09", data: null };
    const headers = {
        "Accept": "application/json, text/plain, */*",
        "Content-Type": "application/json",
        "Device-Id": getDeviceId(),
        "Language-Type": "zh_CN",
        "Platform-Type": "h5_browser",
        "Trade-Area-Type": "0",
        "Trans-Id": getTransId(),
        "Referer": getYstHtmlReferer(),
        "User-Agent": "Mozilla/5.0 (Linux; Android 10; K) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/124.0.0.0 Mobile Safari/537.36"
    };
    if (token)
        headers["User-Token"] = token;
    if (phone)
        headers["X-Cloudbase-Phone"] = phone;
    const signData = await generateDeviceSign();
    headers["X-Device-Id"] = signData.deviceId;
    headers["X-Device-Ts"] = signData.ts;
    headers["X-Device-Sign"] = signData.sign;
    if (method === "POST" && !data)
        delete headers["Content-Type"];
    let url = String(baseUrl || "").replace(/\/$/, "") + path;
    if (method === "GET" && data) {
        const params = new URLSearchParams();
        Object.keys(data).forEach((key) => {
            if (data[key] !== null && data[key] !== void 0)
                params.append(key, data[key]);
        });
        const qs = params.toString();
        if (qs)
            url += "?" + qs;
    }
    const NativeHttp = getHttp();
    const maxAttempts = 2;
    let lastError = null;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
            let resData;
            if (NativeHttp) {
                const options = {
                    url,
                    method,
                    headers,
                    data: method !== "GET" && data ? data : void 0
                };
                const response = await NativeHttp.request(options);
                resData = response.data;
            }
            else {
                const config = { method, headers };
                if (method !== "GET" && data)
                    config.body = JSON.stringify(data);
                const response = await fetch(url, config);
                resData = await response.json();
            }
            if (typeof resData === "string") {
                try {
                    resData = JSON.parse(resData);
                }
                catch (e) {
                }
            }
            if (resData && typeof resData === "object") {
                const c = resData.code;
                if (isAuthTokenExpired(resData) && c !== 1) {
                    console.warn("[LST] Token \u5DF2\u8FC7\u671F:", resData.code, resData.message);
                    const stored = getLstToken();
                    if (token && stored && token !== stored) {
                        return resData;
                    }
                    if (_tokenExpiredHandler)
                        _tokenExpiredHandler(resData.message);
                    return resData;
                }
            }
            if (_isRetryableResponse(resData) && attempt < maxAttempts - 1) {
                console.warn(`[LST] ${path} \u670D\u52A1\u7AEF\u9650\u6D41/\u65E0\u6570\u636E\uFF0C${getAutoPullDelayMs()}ms \u540E\u91CD\u8BD5`);
                await sleepMs(getAutoPullDelayMs());
                continue;
            }
            return resData;
        }
        catch (error) {
            lastError = error;
            console.error(`API Error (attempt ${attempt + 1}/${maxAttempts}):`, error);
            if (attempt < maxAttempts - 1) {
                await sleepMs(getAutoPullDelayMs());
            }
        }
    }
    throw new Error("\u7F51\u7EDC\u8BF7\u6C42\u5931\u8D25: " + (lastError?.message || "\u672A\u77E5\u9519\u8BEF"));
};
const _inflightGET = new Map();
const request = async (path, method = "GET", data = null, token = "", phone = "") => {
    const baseUrl = getApiBase();
    if (method !== "GET") {
        return apiRequest(baseUrl, path, method, data, token, phone);
    }
    const authTag = `${String(token || "").slice(-16)}|${String(phone || "")}`;
    const dedupKey = baseUrl + "|" + path + "|" + JSON.stringify(data ?? {}) + "|" + authTag;
    const existing = _inflightGET.get(dedupKey);
    if (existing)
        return existing;
    const p = apiRequest(baseUrl, path, method, data, token, phone).finally(() => {
        _inflightGET.delete(dedupKey);
    });
    _inflightGET.set(dedupKey, p);
    return p;
};
export const login = (phoneNumber, code) => request("/nft-web/v1.1/nft/user/login", "POST", { phoneNumber, code, inviteCode: "" });
export const loginByPassword = (phoneNumber, password) => request("/nft-web/v1.2/nft/user/login", "POST", { phoneNumber, password });
/** 图形验证码（算术题）；不走 GET 去重，便于点击刷新重新拉取 */
export const getCaptchaCode = (phone) => apiRequest(getApiBase(), "/nft-web/captcha/code", "GET", { phone }, "", phone);
/**
 * 发送登录短信验证码（图形校验通过后）。
 * jingquan 平台使用查询参数传递 cKey/code；LST 使用 JSON body 传递。
 */
export const sendLoginCodeSms = (phone, cKey, code) => {
    const platformId = getStoredPlatformId();
    if (platformId === "jq18") {
        const qs = `?cKey=${encodeURIComponent(String(cKey))}&code=${encodeURIComponent(String(code))}`;
        return request(`/nft-web/nft/user/sendLoginCode${qs}`, "POST", null, "", phone);
    }
    return request("/nft-web/nft/user/sendLoginCode", "POST", {
        phoneNumber: String(phone),
        cKey: String(cKey),
        code: String(code)
    }, "", phone);
};
export const getUserInfo = (token, phone) => request("/nft-web/v1.1/nft/user/getUserInfo", "POST", {}, token, phone);
export const checkToken = (token, phone) => request("/nft-web/nft/user/checkToken", "POST", { token }, token, phone);
export const getNftHomePage = (token = "", phone = "") => request("/nft-web/nft/home/page", "GET", null, token, phone);
/** 镜权集卡：盲盒/集卡活动列表（GET，data 为数组） */
export const getBlindCollectList = (token, phone) => request("/nft-web/blind/collect/getBlindCollectList", "GET", null, token, phone);
export const getTradeAreaConfig = (token = "", phone = "") => request("/nft-web/nft/app/getTradeAreaConfig", "GET", null, token, phone);
export const getAlbumSearch = (token, phone, page = 1, pageSize = 10, extra = {}) => request("/nft-web/v1.2/nft/product/getAlbumSearch", "GET", { page, pageSize, ...extra }, token, phone);
export const getHomeActivity = (token, phone, id) => request("/nft-web/nft/home/activity", "GET", { id }, token, phone);
export const getDrawList = (token, phone) => request("/nft-web/v1/nft/draw/list", "GET", null, token, phone);
export const postDrawInfo = (token, phone, drawId) => request("/nft-web/v1/nft/draw/info", "POST", { drawId }, token, phone);
export const postDrawAlbumInfo = (token, phone, drawId) => request("/nft-web/v1/nft/draw/drawAlbumInfo", "POST", { drawId }, token, phone);
export const postUserDrawNum = (token, phone, drawId) => request("/nft-web/v1/nft/draw/userDrawNum", "POST", { drawId }, token, phone);
export const postDrawPointNum = (token, phone, drawId) => request("/nft-web/v1/nft/draw/pointNum/", "POST", { drawId }, token, phone);
export const postDrawN = (token, phone, body) => request("/nft-web/v1/nft/draw/drawN", "POST", body, token, phone);
export const postDrawRecordStatistics = (token, phone, drawId) => request("/nft-web/v1/nft/draw/recordStatistics", "POST", { drawId }, token, phone);
export const postDrawPageRecord = (token, phone, body) => request("/nft-web/v1/nft/draw/pageRecord", "POST", body, token, phone);
export const getSyntheticGoodsList = (token, phone, activityId) => request("/nft-web/v1.2/nft/product/getSyntheticGoodsList", "GET", { activityId }, token, phone);
export const getMaxSyntheticNum = (token, phone, activityId) => request("/nft-web/nft/product/maxSyntheticNum", "GET", { activityId }, token, phone);
export const syntheticNewGoods = (token, phone, body) => request("/nft-web/nft/product/syntheticNewGoods", "POST", body, token, phone);
export const batchSynthetic = (token, phone, body) => request("/nft-web/nft/product/batchSynthetic", "POST", body, token, phone);
export const getRecastGoodsList = (token, phone, activityId) => request("/nft-web/v1.2/nft/product/getRecastGoodsList", "GET", { activityId }, token, phone);
export const recastNewGoods = (token, phone, body) => request("/nft-web/nft/product/recastNewGoods", "POST", body, token, phone);
export const getRecastTargetByPage = (token, phone, body) => request("/nft-web/v1/nft/order/getRecastTargetByPage", "POST", body, token, phone);
export const openSyntheticMember = (token, phone, body) => request("/nft-web/nft/product/openSyntheticMember", "POST", body, token, phone);
export const getSyntheticMemberInfo = (token, phone) => request("/nft-web/nft/product/getSyntheticMemberInfo", "GET", null, token, phone);
export const updateSyntheticAutoSwitch = (token, phone, body) => request("/nft-web/nft/product/updateAutoSwitch", "POST", body, token, phone);
export const updateSyntheticMainSwitch = (token, phone, body) => request("/nft-web/nft/product/updateMainSwitch", "POST", body, token, phone);
export const getSyntheticCutDetail = (token, phone, params) => request("/nft-web/nft/web/synthetic/cut/detail", "GET", params, token, phone);
export const getSyntheticCutList = (token, phone, params) => request("/nft-web/nft/web/synthetic/cut/list", "GET", params, token, phone);
export const getSyntheticCutGen = (token, phone, params) => request("/nft-web/nft/web/synthetic/cut/gen", "GET", params, token, phone);
export const syntheticCutJoin = (token, phone, body) => request("/nft-web/nft/web/synthetic/cut/join", "POST", body, token, phone);
export const queryMyPoints = (token, phone) => request("/nft-web/nft/points/queryMyPoints", "GET", null, token, phone);
export const getPointRemind = (token, phone, params) => request("/nft-web/nft/points/getPointRemind", "GET", params, token, phone);
export const pointsAlbumPageList = (token, phone, body) => request("/nft-web/nft/points/albumPageList", "POST", body, token, phone);
export const pointsSwPageList = (token, phone, body) => request("/nft-web/nft/points/sw/pageList", "POST", body, token, phone);
export const pointsTransactionList = (token, phone, body) => request("/nft-web/nft/points/transaction/list", "POST", body, token, phone);
export const pointsTransfer = (token, phone, body) => request("/nft-web/nft/points/trans", "POST", body, token, phone);
export const getPointsTransCfg = (token, phone) => request("/nft-web/nft/points/transCfg", "GET", null, token, phone);
export const pointsAlbumExchange = (token, phone, body) => request("/nft-web/nft/points/album/exchange", "POST", body, token, phone);
export const pointsSwCreateOrder = (token, phone, body) => request("/nft-web/nft/points/sw/createOrder", "POST", body, token, phone);
export const getPointsAlbumInfo = (token, phone, albumId) => request("/nft-web/nft/points/album/info", "GET", { albumId }, token, phone);
export const getPointsSwInfo = (token, phone, swId) => request(`/nft-web/nft/points/sw/info/${encodeURIComponent(swId)}`, "GET", null, token, phone);
export const getPointsNeterogenyProductDetail = (token, phone, params) => request("/nft-web/nft/points/neterogeny/good/getProductDetail", "GET", params, token, phone);
export const getPointsAllowTrans = (token, phone, diamondType) => request(`/nft-web/nft/points/allowTrans/${encodeURIComponent(diamondType)}`, "GET", null, token, phone);
export const getPointsOrderQuerySearchParamV2 = (token, phone) => request("/nft-web/nft/points/order/querySearchParam/v2", "GET", null, token, phone);
export const getMyOrderTabV2 = (token, phone) => request("/nft-web/nft/order/myOrderTab/v2", "GET", null, token, phone);
export const getWalletTransaction = (token, phone, type = 1, page = 1) => request("/nft-web/nft/wallet/tx/transaction", "POST", { page, pageSize: 20, type }, token, phone);
export const getWalletBalance = (token, phone) => request("/nft-web/nft/wallet/balance", "GET", null, token, phone);
export const normalizeWalletBalance = (res) => {
    const d = res?.data;
    if (Array.isArray(d) && d.length > 0)
        return d[0];
    if (d && typeof d === "object")
        return d;
    return null;
};
export const getCollectionListByUid = (token, phone, uid, type = 1, name = null, page = 1, pageSize = 100) => request("/nft-web/v1.4/nft/product/getCollectionListByUidV4", "POST", {
    uid,
    page,
    pageSize,
    type,
    nftType: 0,
    ...name ? { name } : {}
}, token, phone);
const COLLECTION_LIST_MAX_PAGES = 80;
export async function fetchAllCollectionListByUid(token, phone, uid, type, name = null, pageSize = 100) {
    const all = [];
    let page = 1;
    while (page <= COLLECTION_LIST_MAX_PAGES) {
        const res = await getCollectionListByUid(token, phone, uid, type, name, page, pageSize);
        if (Number(res?.code) !== 1 || !res.data)
            break;
        const batch = Array.isArray(res.data.list) ? res.data.list : [];
        all.push(...batch);
        const h = res.data.hashNext ?? res.data.hasNext;
        const hasNext = h === 1 || h === "1" || h === true;
        if (!hasNext || batch.length < pageSize)
            break;
        page++;
    }
    return all;
}
/** 专辑内在售编号列表；onSale:1 与主站一致，表示筛选寄售/在售挂牌 */
export const getProductListByAlbumId = (token, phone, albumId, page = 1) => request("/nft-web/v1.2/nft/product/getProductListByAlbumId", "GET", {
    albumId,
    page,
    pageSize: 50,
    order: 1,
    onSale: 1,
    nftType: 0
}, token, phone);
export const getHeadAlbumDetails = (token, phone, albumId, marketType = 1) => request("/nft-web/v1.2/nft/product/getHeadAlbumDetails", "GET", {
    albumId,
    marketType
}, token, phone);
export const getAllHeadList = (token, phone, albumId, page, pageSize = 100, numSort = 0) => request("/nft-web/v1.2/nft/product/getAllHeadList", "GET", {
    albumId,
    pageSize,
    page,
    numSort
}, token, phone);
export const getProductDetail = (token, phone, albumId, gId) => request("/nft-web/v1.2/nft/product/getProductDetail", "GET", { albumId, gId }, token, phone);
export const getTransactionList = (token, phone, albumId, gNum, page = 1, pageSize = 50) => request("/nft-web/v1.2/nft/product/getTransactionList", "GET", { page, pageSize, albumId, gNum }, token, phone);
export const searchMarket = (token, phone, content, page = 1, pageSize = 20, nftType = 0) => request("/nft-web/nft/market/search", "GET", { page, pageSize, content, nftType }, token, phone);
export const createFirstOrder = (token, phone, body) => request("/nft-web/order/nft/order/create", "POST", body, token, phone);
export const panicBuyOrder = (token, phone, body) => request("/nft-web/nft/order/panicBuy", "POST", body, token, phone);
export const createQuickOrder = (token, phone, albumId, price) => request("/nft-web/v1/nft/quick/order/create", "POST", { albumId, type: 0, price: parseInt(price) }, token, phone);
export const createNftOrder = (token, phone, { albumId, gId, gNum, price, type = 0 }) => request("/nft-web/v1/nft/order/create", "POST", {
    albumId: Number(albumId),
    gId: Number(gId),
    type: Number(type),
    gNum: Number(gNum),
    price: price != null ? String(price) : ""
}, token, phone);
/** @deprecated 镜权请用 orderPayJq / payWithBalance；LST 等仍可用 consignPass 直传 */
export const payOrder = (token, phone, orderId, consignPass) => request("/nft-web/v1/nft/order/pay", "GET", { orderId, consignPass }, token, phone);

/**
 * 镜权订单支付（GET）。余额支付需传 payChannel=27 与 checkConsignPass 返回的 resaleUuId。
 */
export const orderPayJq = (token, phone, { orderId, payChannel, resaleUuId }) => {
    const params = { orderId: String(orderId), payChannel: Number(payChannel) };
    if (resaleUuId != null && String(resaleUuId).trim() !== "") {
        params.resaleUuId = resaleUuId;
    }
    return request("/nft-web/v1/nft/order/pay", "GET", params, token, phone);
};

/** 抢购/快捷单继续支付（镜权 H5 与 demo 中 @v1/quick/order/payContinue 一致） */
export const quickOrderPayContinue = (token, phone, payload = {}) => request("/nft-web/v1/quick/order/payContinue", "POST", payload, token, phone);
export const getOrderDetail = (token, phone, orderId, createTime) => request("/nft-web/v1/nft/order/detail", "GET", { orderId, createTime }, token, phone);
/** 镜权等平台：首发/专辑批量下单（数量与弹窗步进器一致，maxBuyCount 为字符串） */
export const createBatchOrder = (token, phone, body) => request("/nft-order/nft/order/createBatch", "POST", body, token, phone);
/** 默认收货/链上存储地址（无数据时接口仍可能 success） */
export const getAddressDef = (token, phone) => request("/nft-web/userAddress/getAddressDef", "GET", null, token, phone);
export const cancelOrder = (token, phone, orderId, createTime) => request("/nft-web/nft/order/cancle", "POST", {
    orderId: String(orderId ?? ""),
    ...createTime != null && String(createTime).trim() !== "" ? { createTime } : {}
}, token, phone);
export const batchLockOrder = (token, phone, body) => request("/nft-web/nft/order/batchLockOrder", "POST", body, token, phone);
export const batchLockingOrderList = (token, phone) => request("/nft-web/nft/order/batchLockingOrderList", "GET", null, token, phone);
export const quickOrderBatchCreate = (token, phone, orderIds, payChannel = 41) => request("/nft-web/v1/nft/quick/order/batchCreate", "POST", { orderIds, payChannel }, token, phone);
export const checkConsignPass = (token, phone, consignPass) => request("/nft-web/nft/user/checkConsignPass", "POST", { consignPass }, token, phone);

/**
 * 镜权余额/积分钱包支付：先校验交易密码，再 orderPay。
 * consignPass 为明文 6 位，不做 MD5。
 */
export const payWithBalance = async (token, phone, orderId, consignPass, payChannel = 27) => {
    const passRes = await checkConsignPass(token, phone, consignPass);
    if (Number(passRes.code) !== 1) {
        return passRes;
    }
    const resaleUuId = passRes.data;
    return orderPayJq(token, phone, { orderId, payChannel, resaleUuId });
};

/** 开通/进入易宝钱包 H5（对应 /account/channel/） */
export const openYeeWallet = (token, phone, payChannel = 23) => request("/nft-web/nft/product/openYeeWallet", "POST", { payChannel: Number(payChannel) }, token, phone);

/**
 * 易宝支付：先 checkConsignPass，再 order/pay?payChannel=22|23&resaleUuId=...
 * 与订单页需密码分支一致；是否已付由 orderPayJq18 查订单 status 判定。
 */
export const payWithYeepay = async (token, phone, orderId, consignPass, payChannel = 23) => {
    const passRes = await checkConsignPass(token, phone, consignPass);
    if (Number(passRes.code) !== 1) {
        return passRes;
    }
    return orderPayJq(token, phone, { orderId, payChannel: Number(payChannel), resaleUuId: passRes.data });
};

/**
 * 从 getGoodsPoundage 返回中选取寄售收款渠道，与官网 goodsSetUpResale.receiveChannels 一致。
 * 官网默认多选：列表内渠道全部选中，以逗号拼接（如 "23,45"）。
 */
export const pickResellReceiveChannels = (poundageData) => {
    const list = poundageData?.payChannelList;
    if (!Array.isArray(list) || list.length === 0)
        return "";
    const sorted = [...list]
        .filter((c) => c != null && c.payChannel != null && String(c.payChannel).trim() !== "")
        .sort((a, b) => (Number(a?.payChannelSort) || 0) - (Number(b?.payChannelSort) || 0));
    if (sorted.length === 0)
        return "";
    return sorted.map((c) => String(c.payChannel).trim()).join(",");
};
/** 未显式传 receiveChannels 时：镜权默认 23、LST 默认 41（有 poundage 列表时由 pickResellReceiveChannels 默认全选）。 */
const defaultResellReceiveChannels = () => (getStoredPlatformId() === PLATFORM_JQ18 ? "23" : "41");
export const shelvesProduct = (token, phone, gId, price, resaleUuId, receiveChannels) => {
    const ch = receiveChannels != null && String(receiveChannels).trim() !== ""
        ? String(receiveChannels).trim()
        : defaultResellReceiveChannels();
    return request("/nft-web/nft/product/goodsSetUpResale", "POST", {
        gId,
        price,
        resaleUuId,
        receiveChannels: ch
    }, token, phone);
};
/** 数字盲盒寄售手续费 / 收款渠道（与官网 getBlindPoundage 一致） */
export const getBlindPoundage = (token, phone, albumId, label = 0) => request("/nft-web/nft/product/getBlindPoundage", "POST", {
    albumId,
    label: Number(label) || 0
}, token, phone);
/** 盲盒详情；带 gId 时返回该盒归属等信息 */
export const getBlindInfo = (token, phone, albumId, gId) => {
    const params = { id: albumId };
    if (gId != null && String(gId).trim() !== "")
        params.gId = gId;
    return request("/nft-web/v1/nft/blind/info", "GET", params, token, phone);
};
/**
 * 数字盲盒寄售上架（官网 blindSetUpResale；receiveChannels 同普通寄售，如 "23,45"）。
 * 官网单次挂售数量上限为 10（maxBuyCount / 接口校验）。
 */
export const BLIND_RESELL_MAX_NUM = 10;
export const shelvesBlindProduct = (token, phone, { albumId, price, num, label = 0, resaleUuId, receiveChannels }) => {
    const ch = receiveChannels != null && String(receiveChannels).trim() !== ""
        ? String(receiveChannels).trim()
        : defaultResellReceiveChannels();
    const n = Math.max(1, Math.floor(Number(num) || 1));
    return request("/nft-web/nft/product/blindSetUpResale", "POST", {
        price,
        albumId,
        label: Number(label) || 0,
        num: Math.min(n, BLIND_RESELL_MAX_NUM),
        resaleUuId,
        receiveChannels: ch
    }, token, phone);
};
export const getGoodsPoundage = (token, phone, gId) => request("/nft-web/nft/product/getGoodsPoundage", "POST", { gId }, token, phone);
export const gotoReSale = (token, phone, albumId) => request("/nft-web/nft/product/gotoReSale", "POST", { albumId }, token, phone);
export const getWarehouseList = (token, phone, albumId, uid, page = 1, pageSize = 200) => request("/nft-web/v1.4/nft/product/getGoodsListByAlbumIdV4", "POST", {
    uid,
    albumId,
    type: 0,
    page,
    pageSize,
    dateRange: null
}, token, phone);
export const searchMyCollection = (token, phone, uid, keyword) => request("/nft-web/v1.4/nft/product/getCollectionListByUidV4", "POST", {
    uid,
    page: 1,
    pageSize: 50,
    type: 0,
    nftType: 0,
    name: keyword
}, token, phone);
export const getMyUnsoldGoods = (token, phone, albumId, uid, page = 1, pageSize = 100) => request("/nft-web/v1.4/nft/product/getGoodsListByAlbumIdV4", "POST", {
    uid,
    albumId,
    type: 0,
    page,
    pageSize,
    dateRange: null
}, token, phone);
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const parseWarehouseListTotal = (data) => {
    if (!data || typeof data !== "object")
        return NaN;
    const candidates = [
        data.total,
        data.totalCount,
        data.totalSize,
        data.recordCount,
        data.recordsTotal,
        data.dataCount,
        data.allCount,
        data.listTotal,
        data.count,
        data.num,
        data.page?.total,
        data.pagination?.total,
        data.pageInfo?.total
    ];
    for (const raw of candidates) {
        const n = Number(raw);
        if (Number.isFinite(n) && n > 0)
            return n;
    }
    return NaN;
};
export const getAllUnsoldGoods = async (token, phone, albumId, uid, onProgress = null) => {
    const PAGE_SIZE = 100;
    const PAGE_GAP_MS = 220;
    const MAX_PAGE = 200;
    let page = 1;
    let allItems = [];
    let hasMore = true;
    const seenGid = new Set();
    let serverTotal = NaN;
    let rawPulled = 0;
    while (hasMore) {
        let res = null;
        let attempt = 0;
        while (attempt < 2) {
            res = await getMyUnsoldGoods(token, phone, albumId, uid, page, PAGE_SIZE);
            if (res.code === 1 && res.data?.list)
                break;
            attempt++;
            if (attempt < 2)
                await sleepMs(getAutoPullDelayMs());
        }
        if (res.code !== 1 || !res.data?.list) {
            break;
        }
        const list = res.data.list;
        if (!Array.isArray(list) || list.length === 0) {
            break;
        }
        rawPulled += list.length;
        for (const row of list) {
            const gid = row?.gId;
            if (gid != null && !seenGid.has(gid)) {
                seenGid.add(gid);
                allItems.push(row);
            }
        }
        const pageTotal = parseWarehouseListTotal(res.data);
        if (Number.isFinite(pageTotal) && pageTotal > 0) {
            serverTotal = pageTotal;
        }
        if (onProgress) {
            onProgress(page, rawPulled);
        }
        if (Number.isFinite(serverTotal) && serverTotal > 0) {
            hasMore = rawPulled < serverTotal;
        }
        else {
            hasMore = list.length >= PAGE_SIZE;
        }
        if (hasMore) {
            page++;
            if (page > MAX_PAGE) {
                hasMore = false;
            }
            else {
                await sleep(PAGE_GAP_MS);
            }
        }
    }
    return allItems;
};
export const getHoldGoodsUntilEnoughForShelves = async (token, phone, albumId, uid, needCount, { preferredGId = null, onProgress = null } = {}) => {
    const PAGE_SIZE = 100;
    const PAGE_GAP_MS = 220;
    const MAX_PAGE = 200;
    const need = Math.max(0, Math.floor(Number(needCount) || 0));
    if (need <= 0)
        return [];
    const pref = preferredGId != null && String(preferredGId).trim() !== "" ? String(preferredGId) : "";
    let page = 1;
    const allItems = [];
    const seenGid = new Set();
    let serverTotal = NaN;
    let rawPulled = 0;
    let hasMore = true;
    const isHold = (row) => Number(row?.onSale) !== 6;
    const holdRows = () => allItems.filter(isHold);
    const hasPreferred = () => !pref || holdRows().some((g) => String(g.gId) === pref);
    while (hasMore) {
        let res = null;
        let attempt = 0;
        while (attempt < 2) {
            res = await getMyUnsoldGoods(token, phone, albumId, uid, page, PAGE_SIZE);
            if (res.code === 1 && res.data?.list)
                break;
            attempt++;
            if (attempt < 2)
                await sleepMs(getAutoPullDelayMs());
        }
        if (res.code !== 1 || !res.data?.list) {
            break;
        }
        const list = res.data.list;
        if (!Array.isArray(list) || list.length === 0) {
            break;
        }
        rawPulled += list.length;
        for (const row of list) {
            const gid = row?.gId;
            if (gid != null && !seenGid.has(gid)) {
                seenGid.add(gid);
                allItems.push(row);
            }
        }
        const pageTotal = parseWarehouseListTotal(res.data);
        if (Number.isFinite(pageTotal) && pageTotal > 0) {
            serverTotal = pageTotal;
        }
        const holdCnt = holdRows().length;
        if (onProgress) {
            onProgress(page, rawPulled, holdCnt);
        }
        if (Number.isFinite(serverTotal) && serverTotal > 0) {
            hasMore = rawPulled < serverTotal;
        }
        else {
            hasMore = list.length >= PAGE_SIZE;
        }
        const exhausted = !hasMore || page >= MAX_PAGE;
        if (exhausted || holdCnt >= need && hasPreferred()) {
            break;
        }
        if (hasMore) {
            page++;
            if (page > MAX_PAGE) {
                hasMore = false;
            }
            else {
                await sleep(PAGE_GAP_MS);
            }
        }
    }
    return holdRows();
};
export const getAllMyGoods = async (token, phone, albumId, uid, onProgress = null) => {
    const PAGE_SIZE = 50;
    const PAGE_GAP_MS = 220;
    const MAX_PAGE = 200;
    let page = 1;
    let allItems = [];
    let hasMore = true;
    const seenGid = new Set();
    while (hasMore) {
        let res = null;
        let attempt = 0;
        while (attempt < 2) {
            res = await getMyGoodsList(token, phone, albumId, uid, page);
            if (res.code === 1 && res.data?.list)
                break;
            attempt++;
            if (attempt < 2)
                await sleepMs(getAutoPullDelayMs());
        }
        if (res.code !== 1 || !res.data?.list) {
            break;
        }
        const list = res.data.list;
        if (!Array.isArray(list) || list.length === 0) {
            break;
        }
        for (const row of list) {
            const gid = row?.gId;
            if (gid != null && !seenGid.has(gid)) {
                seenGid.add(gid);
                allItems.push(row);
            }
        }
        if (onProgress) {
            onProgress(page, allItems.length);
        }
        const totalRaw = res.data.total ?? res.data.totalCount ?? res.data.totalSize;
        const total = Number(totalRaw);
        const hn = res.data.hashNext ?? res.data.hasNext;
        if (hn === 1 || hn === "1") {
            hasMore = true;
        }
        else if (hn === 0 || hn === "0") {
            hasMore = false;
        }
        else if (Number.isFinite(total) && total > 0) {
            hasMore = allItems.length < total;
        }
        else {
            hasMore = list.length >= PAGE_SIZE;
        }
        if (hasMore) {
            page++;
            if (page > MAX_PAGE) {
                hasMore = false;
            }
            else {
                await sleep(PAGE_GAP_MS);
            }
        }
    }
    return allItems;
};
export const getAllClassify = () => request("/nft-web/nft/classify/getAllClassify", "GET");
export const getSearchClass = () => request("/nft-web/nft/product/searchClass", "GET");
export const getMarketList = (token, phone, params) => request("/nft-web/nft/market", "GET", params, token, phone);
export const getJingTanMarketAlbumList = (token, phone, page = 1, pageSize = 20) => request("/nft-web/jingTan/marketAlbumList", "POST", { page, pageSize }, token, phone);
export const getXunFengMarketAlbumList = (token, phone, page = 1, pageSize = 20) => request("/nft-web/xunFeng/marketAlbumList", "POST", { page, pageSize }, token, phone);
export const getJingTanGoodsList = (token, phone, { page = 1, pageSize = 50, order = 0, albumId }) => request("/nft-web/jingTan/goodsList", "POST", {
    page,
    pageSize,
    order,
    albumId: String(albumId ?? "")
}, token, phone);
export const getXunFengGoodsList = (token, phone, { page = 1, pageSize = 50, order = 0, albumId }) => request("/nft-web/xunFeng/goodsList", "POST", {
    page,
    pageSize,
    order,
    albumId: String(albumId ?? "")
}, token, phone);
export const getJingTanGoodsDetail = (token, phone, albumId, orderId, jtNum = "") => request("/nft-web/jingTan/getGoodsDetail", "GET", {
    albumId,
    orderId: String(orderId ?? ""),
    jtNum: String(jtNum ?? "")
}, token, phone);
export const getXunFengGoodsDetail = (token, phone, albumId, orderId, jtNum = "") => request("/nft-web/xunFeng/getGoodsDetail", "GET", {
    albumId,
    orderId: String(orderId ?? ""),
    jtNum: String(jtNum ?? "")
}, token, phone);
export const queryMyFocusList = (token, phone, params) => request("/nft-web/nft/product/queryMyFocusList", "GET", params, token, phone);
export const focusOnAlbum = (token, phone, body) => request("/nft-web/nft/product/focusOnAlbum", "POST", body, token, phone);
export const getAlbumInfo = (token, phone, albumId) => request("/nft-web/v1.2/nft/album/info", "GET", { albumId }, token, phone);
export const getOnSaleList = (token, phone, albumId, page = 1, order = 1) => request("/nft-web/v1.2/nft/product/getProductListByAlbumId", "GET", {
    albumId,
    page,
    pageSize: 50,
    order,
    onSale: 1,
    nftType: 0
}, token, phone);
export const batchCancelResale = (token, phone, albumId, gIds) => request("/nft-web/nft/product/batchCancelResale", "POST", {
    albumId,
    gIds,
    type: 0
}, token, phone);
export const getMyGoodsList = (token, phone, albumId, uid, page = 1) => request("/nft-web/v1.4/nft/product/getGoodsListByAlbumIdV4", "POST", {
    uid,
    albumId,
    type: 0,
    page,
    pageSize: 50,
    dateRange: null
}, token, phone);
const PRICE_API = SERVICE_BASE_URL;
const priceRequest = async (path, method = "GET", body = null) => {
    const url = `${PRICE_API}${path}`;
    const NativeHttp = getHttp();
    const maxAttempts = 2;
    let lastErr = null;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
            const signData = await generateDeviceSign();
            const headers = {
                "Content-Type": "application/json",
                "X-Device-Id": signData.deviceId,
                "X-Device-Ts": signData.ts,
                "X-Device-Sign": signData.sign
            };
            if (NativeHttp) {
                const opts = { url, method, headers };
                if (body)
                    opts.data = body;
                const resp = await NativeHttp.request(opts);
                return typeof resp.data === "string" ? JSON.parse(resp.data) : resp.data;
            }
            else {
                const opts = { method, headers };
                if (body)
                    opts.body = JSON.stringify(body);
                const resp = await fetch(url, opts);
                return await resp.json();
            }
        }
        catch (e) {
            lastErr = e;
            console.warn(`[PriceAPI] ${path} \u5931\u8D25 (attempt ${attempt + 1}/${maxAttempts}):`, e.message);
            if (attempt < maxAttempts - 1) {
                await sleepMs(getAutoPullDelayMs());
            }
        }
    }
    console.warn(`[PriceAPI] ${path} \u653E\u5F03:`, lastErr?.message);
    return { success: false };
};
export const reportPrice = (data) => priceRequest("/price/report", "POST", data);
export const batchReportPrice = (items) => priceRequest("/price/batch-report", "POST", { items });
export const getSnapshotStatus = (albumIds) => priceRequest(`/price/snapshot-status?albumIds=${albumIds.join(",")}`);
export const getPriceChange = (albumIds) => priceRequest(`/price/change?albumIds=${albumIds.join(",")}`);
export const getAllPriceChange = () => priceRequest("/price/change?all=1");
export const getPointsOrderListV2 = (token, phone, body) => request("/nft-web/nft/points/order/list/v2", "POST", body, token, phone);
export const getPriceHistory = (albumId, days = 30) => priceRequest(`/price/history?albumId=${albumId}&days=${days}`);
export const getPriceRank = (type = "down", limit = 50) => priceRequest(`/price/rank?type=${type}&limit=${limit}`);
export const getHallMeta = () => priceRequest("/hall/meta", "GET");
export const getHallRank = (type = "buy") => priceRequest(`/hall/rank?type=${encodeURIComponent(type)}`, "GET");
export const postHallTradeReport = (trades) => priceRequest("/hall/trade-report", "POST", {
    sessionToken: getSessionToken() || "",
    trades
});
export const getMyUnsoldProducts = getWarehouseList;
export default {
    login,
    loginByPassword,
    getUserInfo,
    checkToken,
    getNftHomePage,
    getTradeAreaConfig,
    getAlbumSearch,
    getHomeActivity,
    getSyntheticGoodsList,
    getMaxSyntheticNum,
    syntheticNewGoods,
    batchSynthetic,
    getRecastGoodsList,
    recastNewGoods,
    getRecastTargetByPage,
    openSyntheticMember,
    getSyntheticMemberInfo,
    updateSyntheticAutoSwitch,
    updateSyntheticMainSwitch,
    getSyntheticCutDetail,
    getSyntheticCutList,
    getSyntheticCutGen,
    syntheticCutJoin,
    queryMyPoints,
    getPointRemind,
    pointsAlbumPageList,
    pointsSwPageList,
    pointsTransactionList,
    pointsTransfer,
    getPointsTransCfg,
    pointsAlbumExchange,
    pointsSwCreateOrder,
    getPointsAlbumInfo,
    getPointsSwInfo,
    getPointsNeterogenyProductDetail,
    getPointsAllowTrans,
    getPointsOrderQuerySearchParamV2,
    getWalletTransaction,
    getWalletBalance,
    normalizeWalletBalance,
    getCollectionListByUid,
    fetchAllCollectionListByUid,
    getProductListByAlbumId,
    getHeadAlbumDetails,
    getAllHeadList,
    getProductDetail,
    getTransactionList,
    searchMarket,
    createFirstOrder,
    panicBuyOrder,
    createQuickOrder,
    createNftOrder,
    payOrder,
    orderPayJq,
    payWithBalance,
    openYeeWallet,
    payWithYeepay,
    quickOrderPayContinue,
    getOrderDetail,
    createBatchOrder,
    getAddressDef,
    cancelOrder,
    batchLockOrder,
    batchLockingOrderList,
    quickOrderBatchCreate,
    checkConsignPass,
    shelvesProduct,
    shelvesBlindProduct,
    pickResellReceiveChannels,
    getGoodsPoundage,
    getBlindPoundage,
    getBlindInfo,
    getWarehouseList,
    getMyUnsoldProducts,
    searchMyCollection,
    getMyUnsoldGoods,
    getAllUnsoldGoods,
    getHoldGoodsUntilEnoughForShelves,
    getAllMyGoods,
    getAllClassify,
    getSearchClass,
    getMarketList,
    getJingTanMarketAlbumList,
    getXunFengMarketAlbumList,
    getJingTanGoodsList,
    getXunFengGoodsList,
    getJingTanGoodsDetail,
    getXunFengGoodsDetail,
    queryMyFocusList,
    focusOnAlbum,
    getAlbumInfo,
    getOnSaleList,
    batchCancelResale,
    getMyGoodsList,
    reportPrice,
    batchReportPrice,
    getSnapshotStatus,
    getPriceChange,
    getAllPriceChange,
    getPriceHistory,
    getPriceRank,
    getHallMeta,
    getHallRank,
    postHallTradeReport,
    getPointsOrderListV2,
    getMyOrderTabV2
};
